import { addMoney, daysBetween, type Money, negateMoney } from '@taler/domain'
import { changeBetween, type KpiChange } from './kpi-change.ts'
import type { Dashboard } from './types.ts'

export interface MonthComparison {
  readonly income: KpiChange
  readonly expense: KpiChange
  readonly net: KpiChange
}

export interface LevelComparison {
  readonly netWorth: KpiChange
  readonly cash: KpiChange
}

/** Month-to-date expense as an outflow: the endpoint returns it as a magnitude. */
export function outflowMonthToDate(dashboard: Dashboard): Money {
  return negateMoney(dashboard.expenseMonthToDate)
}

/** Month-to-date income less expense, both server figures in the one display currency. */
export function netMonthToDate(dashboard: Dashboard): Money {
  return addMoney(dashboard.incomeMonthToDate, outflowMonthToDate(dashboard))
}

/**
 * Month-to-date figures against the same point of last month. Both dashboards are server
 * figures in the one display currency; the only arithmetic here is same-currency comparison.
 */
export function compareSamePointLastMonth(
  current: Dashboard,
  previous: Dashboard,
): MonthComparison | undefined {
  if (current.displayCurrency !== previous.displayCurrency) return undefined
  const days = daysBetween(previous.asOf, current.asOf)
  return {
    income: changeBetween(previous.incomeMonthToDate, current.incomeMonthToDate, days),
    expense: changeBetween(previous.expenseMonthToDate, current.expenseMonthToDate, days),
    net: changeBetween(netMonthToDate(previous), netMonthToDate(current), days),
  }
}

/** Net worth and cash at the same point last month, against today's. */
export function compareLevelsSamePointLastMonth(
  current: Dashboard,
  previous: Dashboard,
): LevelComparison | undefined {
  if (current.displayCurrency !== previous.displayCurrency) return undefined
  const days = daysBetween(previous.asOf, current.asOf)
  return {
    netWorth: changeBetween(previous.netWorth, current.netWorth, days),
    cash: changeBetween(previous.cash, current.cash, days),
  }
}
